import { ArrowDownRight, ArrowUpRight, FileText, Route, Truck, User } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { DataCard } from "@/components/ui/data-card";
import { OrderStatusBadge } from "@/components/ui/status-badge";
import { formatWindowCompact } from "@/lib/order-time";
import { useViewerTimezone } from "@/hooks/useViewerTimezone";
import type { OrderStatus } from "@/types/tms";
import type { DuplicateOrderRow } from "@/hooks/useDuplicateOrderPairs";

/** Render a start..end window in the viewer's timezone; "—" when empty. */
function fmtWindow(startIso: string | null | undefined, endIso: string | null | undefined, viewerTz: string) {
  return formatWindowCompact(startIso, endIso, viewerTz, "MMM d", "HH:mm") || "—";
}

function sourceLabel(o: DuplicateOrderRow): { label: string; variant: "default" | "secondary" } {
  if (o.is_api_created) return { label: "API created", variant: "default" };
  return { label: "Manually created", variant: "secondary" };
}

function locLabel(loc: DuplicateOrderRow["ship_from_location"]) {
  if (!loc) return "—";
  return loc.short_code || loc.name || "—";
}

export function OrderComparisonCard({
  order,
  highlight,
}: {
  order: DuplicateOrderRow;
  highlight?: boolean;
}) {
  const viewerTz = useViewerTimezone();
  const src = sourceLabel(order);
  const customerName =
    order.billing_party?.organization_name ?? order.billing_party?.short_code ?? null;
  const legs = order.legs ?? [];
  const isMultiLeg = legs.length > 1;

  return (
    <DataCard selected={highlight} className="hover:bg-card cursor-default">
      <DataCard.Header>
        <span className="min-w-0 flex-1 font-mono font-bold text-lg leading-none text-foreground truncate">
          {order.unique_id ?? order.id.slice(0, 8)}
        </span>
        <Badge variant={src.variant}>{src.label}</Badge>
      </DataCard.Header>
      {customerName && (
        <div className="text-sm text-muted-foreground truncate [font-variant-caps:all-small-caps] -mt-1">
          {customerName}
        </div>
      )}

      <DataCard.Chips>
        {order.status ? <OrderStatusBadge status={order.status as OrderStatus} /> : null}
        {order.trip_type ? (
          <Badge variant="outline" className="font-normal">
            {order.trip_type}
          </Badge>
        ) : null}
      </DataCard.Chips>

      <DataCard.Body>
        <DataCard.Field
          icon={ArrowUpRight}
          secondary={fmtWindow(order.ready_time, order.ready_time_end, viewerTz)}
        >
          {locLabel(order.ship_from_location)}
        </DataCard.Field>
        <DataCard.Field
          icon={ArrowDownRight}
          secondary={fmtWindow(
            order.requested_delivery_time,
            order.requested_delivery_time_end,
            viewerTz,
          )}
        >
          {locLabel(order.ship_to_location)}
        </DataCard.Field>
      </DataCard.Body>

      <div className="flex flex-col gap-2">
        <DataCard.Tile icon={FileText} label="Tender ID" value={order.tender_id} />
      </div>

      {isMultiLeg && (
        <div className="rounded-md border bg-muted/40 p-3">
          <div className="flex items-center gap-2 mb-2 text-xs font-medium text-muted-foreground [font-variant-caps:all-small-caps]">
            <Route className="h-3.5 w-3.5" />
            {legs.length} legs
          </div>
          <div className="flex flex-col gap-2">
            {legs.map((leg, i) => (
              <DataCard key={leg.id} className="hover:bg-card cursor-default">
                <DataCard.Header>
                  <span className="flex items-baseline gap-2 min-w-0">
                    <span className="font-bold text-lg leading-tight text-foreground truncate py-0.5">
                      Leg {leg.leg_sequence ?? i + 1}
                    </span>
                    <span className="font-mono text-sm text-muted-foreground truncate">
                      {order.unique_id ?? order.id.slice(0, 8)}
                    </span>
                  </span>
                </DataCard.Header>

                {(leg.trip_type || leg.customer_leg_id) && (
                  <DataCard.Chips>
                    {leg.trip_type ? (
                      <Badge variant="outline" className="font-normal">
                        {leg.trip_type}
                      </Badge>
                    ) : null}
                    {leg.customer_leg_id ? (
                      <Badge variant="outline" className="font-normal">
                        Mission {leg.customer_leg_id}
                      </Badge>
                    ) : null}
                  </DataCard.Chips>
                )}

                <DataCard.Body>
                  <DataCard.Field
                    icon={ArrowUpRight}
                    secondary={fmtWindow(leg.ready_time, leg.ready_time_end, viewerTz)}
                  >
                    {leg.ship_from_label}
                  </DataCard.Field>
                  <DataCard.Field
                    icon={ArrowDownRight}
                    secondary={fmtWindow(
                      leg.requested_delivery_time,
                      leg.requested_delivery_time_end,
                      viewerTz,
                    )}
                  >
                    {leg.ship_to_label}
                  </DataCard.Field>
                </DataCard.Body>

                <div className="flex flex-col gap-2">
                  <DataCard.Tile
                    icon={Truck}
                    label="Truck ID"
                    value={
                      leg.tractor_name ?? (
                        <span className="text-muted-foreground font-normal">No truck assigned</span>
                      )
                    }
                  />
                  <DataCard.Tile
                    icon={User}
                    label="Operator name(s)"
                    value={
                      leg.operator_names.length > 0 ? (
                        leg.operator_names.join(", ")
                      ) : (
                        <span className="text-muted-foreground font-normal">
                          No operators assigned
                        </span>
                      )
                    }
                  />
                </div>
              </DataCard>
            ))}
          </div>
        </div>
      )}
    </DataCard>
  );
}